import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { UserPlus, Eye, EyeOff, AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const { signUp, isLoading, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (password !== confirmPassword) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    if (password.length < 8) {
      setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
      return;
    }

    try {
      await signUp(email, password);
      setMessage({
        type: 'success',
        text: 'Account created. Redirecting…',
      });
      // If Supabase returns a session, AuthContext sets user and useEffect sends you to the dashboard.
      // If email confirmation is required, stay here and sign in from /login after confirming.
    } catch (error: any) {
      console.error('Signup error:', error);
      const errorMessage =
        error?.message || error?.error?.message || error?.toString() || 'Failed to create account. Please try again.';
      const errorString = JSON.stringify(error || {}).toLowerCase();
      const errorCode = error?.code || error?.error?.code || '';

      const duplicateEmailIndicators = [
        'already exists',
        'already registered',
        'email address is already',
        'Email already registered',
        'A user with this email already exists',
        'user with this email address has already been registered',
        'user already',
      ];

      const isDuplicateEmail =
        duplicateEmailIndicators.some(
          (indicator) =>
            errorMessage.toLowerCase().includes(indicator.toLowerCase()) ||
            errorString.includes(indicator.toLowerCase())
        ) ||
        errorCode === 'signup_disabled' ||
        errorCode === 'email_already_exists' ||
        errorString.includes('already');

      if (isDuplicateEmail) {
        setMessage({
          type: 'error',
          text: 'An account with this email already exists. Please sign in instead.',
        });
      } else {
        setMessage({ type: 'error', text: errorMessage });
      }
    }
  };

  return (
    <div className="min-h-screen bg-cashy-purple-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="w-8 h-8 flex items-end justify-center gap-1 px-1 flex-shrink-0">
              <div className="w-1.5 bg-cashy-purple-400 rounded-sm" style={{ height: '14px' }}></div>
              <div className="w-1.5 bg-cashy-purple-500 rounded-sm" style={{ height: '18px' }}></div>
              <div className="w-1.5 bg-cashy-purple-600 rounded-sm" style={{ height: '22px' }}></div>
            </div>
            <h1 className="text-2xl font-bold text-gray-900">cashy.so</h1>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Create your account</h2>
          <p className="mt-2 text-gray-600">Sign up with your work email</p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {message && (
            <div
              className={`p-4 rounded-lg flex items-start gap-3 ${
                message.type === 'error'
                  ? 'border border-red-500 text-red-600'
                  : 'bg-green-500 bg-opacity-20 border border-green-500 text-green-400'
              }`}
            >
              <AlertCircle size={20} className="mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">{message.text}</p>
                {message.type === 'error' && message.text.includes('already exists') && (
                  <p className="text-sm mt-1 opacity-90">
                    <Link to="/login" className="underline hover:no-underline">
                      Click here to sign in
                    </Link>
                  </p>
                )}
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cashy-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500"
                placeholder="Enter your email"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cashy-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500 pr-12"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cashy-purple-500 focus:border-transparent text-gray-900 placeholder-gray-500 pr-12"
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full bg-cashy-purple-500 hover:bg-cashy-purple-600 disabled:bg-gray-400 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <UserPlus size={20} />
                Create Account
              </>
            )}
          </button>

          <div className="text-center">
            <p className="text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-cashy-purple-500 hover:text-cashy-purple-600 transition-colors">
                Sign in
              </Link>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
